/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';

import { DEFAULT_LANGUAGE } from '../../constants/constants';

import { formats, getLocaleMessageData } from './i18nUtils';

const propTypes = {
  /** children to render must be a node */
  children: PropTypes.node.isRequired,
  /** optionally pass in the user preferences from outside, otherwise we will detect from browser */
  preferences: PropTypes.shape({
    locale: PropTypes.shape({
      country: PropTypes.string,
      language: PropTypes.string,
    }),
    timezone: PropTypes.string,
  }),
};

const defaultProps = {
  preferences: {
    locale: {
      country: null,
      language: null,
    },
    timezone: null,
  },
};

/** loads the locale and messages and sets the correct HTML language for the page */
const IntlProviderWithLocale = ({ children, preferences }) => {
  const [locale, setLocale] = useState(DEFAULT_LANGUAGE);
  const [messages, setMessages] = useState({});
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone); // use the browser timezone by default

  useEffect(() => {
    const { locale: preferencesLocale, timezone } = preferences;

    const language = preferencesLocale?.language
      ? [
          `${preferencesLocale.language}${
            preferencesLocale.country && preferencesLocale.country !== ''
              ? `-${preferencesLocale.country.toLowerCase()}`
              : ''
          }`,
        ]
      : undefined;

    // getLocaleMessageData automatically detects languages from the browser if language isn't set
    getLocaleMessageData(language).then(
      successResponse => {
        const { locale, messages } = successResponse; // eslint-disable-line @typescript-eslint/no-shadow
        if (timezone) {
          setTimeZone(timezone); // if timezone set in the user profile
        }

        setLocale(locale);
        setMessages(messages);
      },
      errorResponse => {
        console.error(errorResponse);
        setLocale('en');
      }
    );
  }, [preferences]);

  return (
    <>
      <Helmet>
        <html lang={locale} />
      </Helmet>
      <IntlProvider locale={locale} messages={messages} formats={formats} timeZone={timeZone}>
        {children}
      </IntlProvider>
    </>
  );
};

IntlProviderWithLocale.defaultProps = defaultProps;
IntlProviderWithLocale.propTypes = propTypes;
export default IntlProviderWithLocale;
